import { BACKEND_BASE, DEFAULT_STATIC_OG_IMAGE } from "@/lib/backend";

export function resolvePublicMediaUrl(url) {
  if (!url) return "";
  if (url.startsWith("http") || url.startsWith("https")) return url;
  if (url.startsWith("/")) {
    if (url.startsWith("/api/")) return `${BACKEND_BASE}${url}`;
    return url;
  }
  return `${BACKEND_BASE}${url}`;
}

export function cacheBust(url, version) {
  if (!url || !version) return url;
  const join = url.includes("?") ? "&" : "?";
  return `${url}${join}v=${encodeURIComponent(version)}`;
}

export function resolvePublicSiteBase(cms, defaults = {}) {
  const fromCms = (cms.seo_canonical_url || cms.seo_og_url || defaults.seo_canonical_url || "").trim();
  if (fromCms) return fromCms.replace(/\/$/, "");
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin.replace(/\/$/, "");
  }
  return "https://dsitesolution.com";
}

/** Stable crawler-facing OG image URL (Kakao/Facebook do not run JavaScript). */
export function resolveOgImageUrl(cms, defaults = {}) {
  const imagePath = cms.media_og_image || defaults.media_og_image;
  const base = resolvePublicSiteBase(cms, defaults);
  const version = cms.updated_at || "1";

  if (imagePath && !imagePath.startsWith("/api/")) {
    const resolved = resolvePublicMediaUrl(imagePath);
    if (resolved && !resolved.includes("/api/public/og-image")) {
      return cacheBust(resolved, version);
    }
  }

  const staticOg = DEFAULT_STATIC_OG_IMAGE.startsWith("http")
    ? DEFAULT_STATIC_OG_IMAGE
    : `${base}${DEFAULT_STATIC_OG_IMAGE}`;
  return cacheBust(staticOg, version);
}

function upsertMetaName(name, content) {
  if (!content) {
    document.querySelector(`meta[name="${name}"]`)?.remove();
    return;
  }
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertMetaProperty(property, content) {
  if (!content) {
    document.querySelector(`meta[property="${property}"]`)?.remove();
    return;
  }
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertHeadLink({ rel, href, type, sizes }) {
  if (!href) return;
  const selector = sizes
    ? `link[rel="${rel}"][sizes="${sizes}"]`
    : `link[rel="${rel}"]`;
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    if (sizes) el.setAttribute("sizes", sizes);
    document.head.appendChild(el);
  }
  if (type) el.type = type;
  else el.removeAttribute("type");
  el.href = href;
}

function upsertScript(id, html) {
  const existing = document.getElementById(id);
  if (!html) {
    existing?.remove();
    return;
  }
  if (existing) existing.remove();
  const el = document.createElement("script");
  el.id = id;
  el.text = html;
  document.head.appendChild(el);
}

function upsertExternalScript(id, src) {
  const existing = document.getElementById(id);
  if (!src) {
    existing?.remove();
    return;
  }
  if (existing?.getAttribute("src") === src) return;
  existing?.remove();
  const el = document.createElement("script");
  el.id = id;
  el.async = true;
  el.src = src;
  document.head.appendChild(el);
}

function upsertNoscript(id, html) {
  const existing = document.getElementById(id);
  if (!html) {
    existing?.remove();
    return;
  }
  if (existing) existing.remove();
  const el = document.createElement("noscript");
  el.id = id;
  el.innerHTML = html;
  document.head.appendChild(el);
}

function upsertJsonLd(id, graphs) {
  const existing = document.getElementById(id);
  if (!graphs || !graphs.length) {
    existing?.remove();
    return;
  }
  if (existing) existing.remove();
  const el = document.createElement("script");
  el.id = id;
  el.type = "application/ld+json";
  el.text = JSON.stringify({ "@context": "https://schema.org", "@graph": graphs });
  document.head.appendChild(el);
}

export function cmsBool(value) {
  return String(value || "").toLowerCase() === "true";
}

let lastGaPath = null;

function normalizeGaId(raw) {
  const text = String(raw || "").trim();
  const match = text.match(/G-[A-Z0-9]+/i);
  return match ? match[0].toUpperCase() : "";
}

function normalizeVerificationToken(raw) {
  const text = String(raw || "").trim();
  if (!text) return "";
  const content = text.match(/content\s*=\s*["']([^"']+)["']/i);
  if (content) return content[1].trim();
  return text.replace(/<meta\b[^>]*>/gi, "").trim().replace(/^[= '"]+|[= '"]+$/g, "");
}

function applyGaMeasurement(rawId, path, gtmOn) {
  const gaId = normalizeGaId(rawId);
  if (gtmOn || !gaId) {
    if (!gtmOn) {
      upsertExternalScript("dsite-seo-ga-loader", "");
      upsertScript("dsite-seo-ga-config", "");
    }
    lastGaPath = null;
    return;
  }
  const existing = document.getElementById("dsite-seo-ga-loader");
  if (!existing) {
    upsertExternalScript(
      "dsite-seo-ga-loader",
      `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gaId)}`,
    );
    upsertScript(
      "dsite-seo-ga-config",
      `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${gaId.replace(/'/g, "\\'")}',{send_page_view:true});`,
    );
    lastGaPath = path;
    return;
  }
  if (lastGaPath === null) {
    lastGaPath = path;
    return;
  }
  if (lastGaPath !== path && typeof window.gtag === "function") {
    lastGaPath = path;
    window.gtag("event", "page_view", {
      page_path: path,
      page_title: document.title,
      page_location: window.location.href,
    });
  }
}

export const HOME_SEO_TITLE = "DSITESOLUTION | 홈페이지 제작 · 웹개발 · AI · 자동화";
export const HOME_OG_TITLE = "DSITESOLUTION | 홈페이지 · 웹개발 · AI · 자동화 · SaaS";

function pagePathKey(path) {
  if (!path) return "/";
  if (path.length > 1 && path.endsWith("/")) return path.slice(0, -1);
  return path;
}

/** React SPA equivalent of Next.js Metadata API — applies CMS SEO to document.head */
export function applySeoHead(cms, defaults = {}, options = {}) {
  const d = defaults;
  const version = cms.updated_at || "1";
  const path = pagePathKey(options.path || (typeof window !== "undefined" ? window.location.pathname : "/"));
  if (path.startsWith("/design-preview")) {
    document.title = "Design Preview (DEV)";
    upsertMetaName("robots", "noindex,nofollow");
    upsertJsonLd("dsite-jsonld", []);
    return;
  }
  const page = options.pageSeo || {};
  const project = options.projectSeo || {};
  const base = resolvePublicSiteBase(cms, d);

  const isHome = path === "/";
  const title =
    page.seo_title ||
    project.seo_title ||
    (isHome ? HOME_SEO_TITLE : "") ||
    cms.site_title ||
    d.site_title;
  if (title) document.title = title;

  const description =
    page.meta_description ||
    project.seo_description ||
    cms.seo_description ||
    d.seo_description;
  upsertMetaName("description", description);
  upsertMetaName("keywords", cms.seo_keywords || d.seo_keywords);
  upsertMetaName("robots", page.robots || project.robots || "index,follow");

  const ogTitle =
    page.og_title ||
    project.og_title ||
    page.seo_title ||
    (isHome ? HOME_OG_TITLE : "") ||
    cms.seo_og_title ||
    cms.site_title ||
    d.seo_og_title ||
    d.site_title;
  const ogDescription =
    page.og_description ||
    project.og_description ||
    page.meta_description ||
    project.seo_description ||
    cms.seo_og_description ||
    cms.seo_description ||
    d.seo_og_description ||
    d.seo_description;
  const pageCanonical =
    page.canonical ||
    project.canonical ||
    (path === "/"
      ? cms.seo_canonical_url || cms.seo_og_url || base
      : `${base}${path}`);
  const ogUrl = pageCanonical || cms.seo_og_url || cms.seo_canonical_url || base;
  const ogImage = page.og_image || project.og_image
    ? resolvePublicMediaUrl(page.og_image || project.og_image)
    : resolveOgImageUrl(cms, d);

  upsertMetaProperty("og:title", ogTitle);
  upsertMetaProperty("og:description", ogDescription);
  upsertMetaProperty("og:type", "website");
  upsertMetaProperty("og:url", ogUrl);
  upsertMetaProperty("og:image", ogImage);
  if (ogImage) {
    upsertMetaProperty("og:image:secure_url", ogImage);
    upsertMetaProperty("og:image:width", "1200");
    upsertMetaProperty("og:image:height", "630");
  }
  upsertMetaProperty("og:locale", "ko_KR");

  upsertMetaName("twitter:card", ogImage ? "summary_large_image" : "summary");
  upsertMetaName("twitter:title", ogTitle);
  upsertMetaName("twitter:description", ogDescription);
  if (ogImage) upsertMetaName("twitter:image", ogImage);

  upsertMetaName("google-site-verification", normalizeVerificationToken(cms.seo_google_verification));
  upsertMetaName("naver-site-verification", normalizeVerificationToken(cms.seo_naver_verification));
  upsertMetaName("msvalidate.01", normalizeVerificationToken(cms.seo_bing_verification));

  if (pageCanonical) upsertHeadLink({ rel: "canonical", href: pageCanonical });

  upsertJsonLd("dsite-jsonld", options.jsonldGraphs || []);

  const faviconUrl = resolvePublicMediaUrl(cms.media_favicon || d.media_favicon);
  if (faviconUrl) {
    const href = cacheBust(faviconUrl, version);
    const lower = faviconUrl.toLowerCase();
    const isSvg = lower.endsWith(".svg");
    const isIco = lower.endsWith(".ico");
    upsertHeadLink({
      rel: "icon",
      href,
      type: isSvg ? "image/svg+xml" : isIco ? "image/x-icon" : undefined,
    });
    if (!isIco) {
      upsertHeadLink({ rel: "icon", href, sizes: "32x32", type: "image/png" });
    }
    upsertHeadLink({ rel: "apple-touch-icon", href, sizes: "180x180" });
  }

  const gtmId = (cms.seo_gtm_id || "").trim();
  const gtmOn = cmsBool(cms.seo_gtm_enabled) && Boolean(gtmId);
  applyGaMeasurement(cms.seo_ga_id || "", path, gtmOn);

  if (gtmOn) {
    upsertScript(
      "dsite-seo-gtm",
      `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${gtmId.replace(/'/g, "\\'")}');`,
    );
  } else {
    upsertScript("dsite-seo-gtm", "");
  }

  const pixelId = (cms.seo_meta_pixel_id || "").trim();
  if (cmsBool(cms.seo_meta_pixel_enabled) && pixelId) {
    upsertScript(
      "dsite-seo-meta-pixel",
      `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${pixelId.replace(/'/g, "\\'")}');fbq('track','PageView');`,
    );
    upsertNoscript(
      "dsite-seo-meta-pixel-noscript",
      `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${encodeURIComponent(pixelId)}&ev=PageView&noscript=1" alt="" />`,
    );
  } else {
    upsertScript("dsite-seo-meta-pixel", "");
    upsertNoscript("dsite-seo-meta-pixel-noscript", "");
  }
}

/** Preview helpers for admin UI */
export function previewOgValues(cms, defaults = {}) {
  const ogTitle = cms.seo_og_title || cms.site_title || defaults.seo_og_title || defaults.site_title || "";
  const ogDescription =
    cms.seo_og_description ||
    cms.seo_description ||
    defaults.seo_og_description ||
    defaults.seo_description ||
    "";
  const ogUrl =
    cms.seo_og_url ||
    cms.seo_canonical_url ||
    defaults.seo_canonical_url ||
    "https://dsitesolution.com";
  const ogImage =
    resolveOgImageUrl(cms, defaults) ||
    resolvePublicMediaUrl(cms.media_og_image || defaults.media_og_image || "");
  return { ogTitle, ogDescription, ogUrl, ogImage };
}
